<script setup lang="ts">
  import type { GenerateEmailBodyData } from '@api/resources/emails';
  import { ref } from 'vue';

  import { Button } from '@ui/components/button';
  import { Command, CommandSeparator } from '@ui/components/command';
  import LoadingSpinner from '@ui/components/LoadingSpinner.vue';
  import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/popover';

  import TaskTemplateGeneration from '@/components/emails/TaskTemplateGeneration.vue';

  interface Props {
    data?: Partial<GenerateEmailBodyData>;
    isGenerating: boolean;
  }

  const props = defineProps<Props>();

  const emits = defineEmits<{
    (e: 'generateBody', options: GenerateEmailBodyData): void;
  }>();

  const isOpen = ref(false);

  const generateWithAI = () => {
    isOpen.value = false;
    emits('generateBody', {
      ...(props.data || {}),
      options: { ...(props.data?.options || {}), use_llm: true },
      email_template: undefined,
    });
  };

  const generateWithTemplate = (templateId: string) => {
    isOpen.value = false;
    emits('generateBody', {
      ...(props.data || {}),
      options: { ...(props.data?.options || {}), use_llm: false },
      email_template: templateId,
    });
  };
</script>

<template>
  <Popover v-model:open="isOpen">
    <PopoverTrigger as-child>
      <Button
        type="button"
        size="xs"
        variant="outline"
        :disabled="isGenerating"
        class="hover:bg-agent/10 hover:border-agent/20"
      >
        <LoadingSpinner v-if="isGenerating" class="text-xs" />
        <i v-else class="fas fa-wand-magic-sparkles text-2xs!" />
        <span>{{ isGenerating ? 'Generating…' : 'Generate' }}</span>
        <i class="fas fa-chevron-down text-2xs!" />
      </Button>
    </PopoverTrigger>

    <PopoverContent align="start" class="w-72 p-0">
      <Command>
        <div class="pt-1.5 pb-1">
          <div
            class="text-2xs uppercase tracking-widest text-muted-foreground font-semibold px-3 pt-1 pb-1.5"
          >
            Generate Reply
          </div>

          <button
            type="button"
            class="w-full flex items-center gap-x-2.5 rounded-sm px-2 py-2 text-left hover:bg-accent hover:text-accent-foreground"
            @click="generateWithAI"
          >
            <div class="w-5 text-center shrink-0">
              <i class="fas fa-sparkles text-agent text-xs" />
            </div>

            <div class="flex flex-col gap-y-0.5">
              <span class="text-xs font-semibold">Generate using AI</span>
              <span class="text-2xs text-muted-foreground">Have agent write a response</span>
            </div>
          </button>
        </div>

        <CommandSeparator />

        <TaskTemplateGeneration
          :show-header="false"
          :is-generating="isGenerating"
          @select="generateWithTemplate"
        />
      </Command>
    </PopoverContent>
  </Popover>
</template>

<style scoped></style>
